import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Mic, Feather } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { ArchiveSubjectIdentity, archiveSubjectDisplayName } from '../archive/ArchiveSubjectIdentity';
import { getStoryCoverUrl, STORY_COVER_VARIANTS } from '../../utils/storyDisplay';

/** Card width in px — keep in sync with layout math below. */
const CARD_W = 308;
const GAP = 22;
const CARD_STEP = CARD_W + GAP;

/**
 * Quiet “memory table”: one fragment at a time, slow pacing, no feed energy.
 * Card-per-story + sliding track stays open for future layers (image, waveform, ambient)
 * without turning into a generic carousel component.
 */
function storyHasVoice(story) {
  if (!story) return false;
  return Boolean(story.audio_file_url) || story.transcript_source === 'voice';
}

function writtenExcerpt(story, maxLen = 120) {
  const t = (story?.raw_transcript || '').trim().replace(/\s+/g, ' ');
  if (!t) return '';
  return t.length > maxLen ? `${t.slice(0, maxLen - 1)}…` : t;
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

/** Emotional anchor: user title leads; prompt is secondary context (falls back to prompt if no title). */
function storyAnchorLines(story) {
  const q = (story?.question_text || '').trim();
  const t = (story?.display_title || '').trim();
  if (t) {
    return {
      primary: t,
      secondary: q ? `Prompted by: “${q}”` : null,
    };
  }
  return { primary: q || 'Story', secondary: null };
}

export function ImmersiveStoryGallery({
  stories = [],
  subject = null,
  activeIndex,
  onActiveIndexChange,
  onOpenStory,
  storyLabel,
  backendBaseUrl = '',
}) {
  const reduceMotion = useReducedMotion();
  const regionRef = useRef(null);
  const trackRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const [viewportW, setViewportW] = useState(800);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || narrow) return undefined;
    const ro = new ResizeObserver(() => {
      setViewportW(Math.max(320, el.clientWidth));
    });
    ro.observe(el);
    setViewportW(Math.max(320, el.clientWidth));
    return () => ro.disconnect();
  }, [narrow, stories.length]);

  /** Slow drift — favors reflection over snappy browsing. */
  const transition = useMemo(
    () =>
      reduceMotion
        ? { type: 'tween', duration: 0.2, ease: 'easeOut' }
        : { type: 'tween', duration: 0.88, ease: [0.2, 0.04, 0.22, 1] },
    [reduceMotion]
  );

  const cardTransition = useMemo(
    () =>
      reduceMotion
        ? { type: 'tween', duration: 0.2, ease: 'easeOut' }
        : { type: 'tween', duration: 0.64, ease: [0.18, 0.03, 0.2, 1] },
    [reduceMotion]
  );

  const trackX = useMemo(() => {
    const half = viewportW / 2;
    const centerSlot = activeIndex * CARD_STEP;
    return half - CARD_W / 2 - centerSlot;
  }, [activeIndex, viewportW]);

  const goPrev = useCallback(() => {
    onActiveIndexChange(Math.max(0, activeIndex - 1));
  }, [activeIndex, onActiveIndexChange]);

  const goNext = useCallback(() => {
    onActiveIndexChange(Math.min(stories.length - 1, activeIndex + 1));
  }, [activeIndex, onActiveIndexChange, stories.length]);

  useEffect(() => {
    const el = regionRef.current;
    if (!el || narrow) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Home') {
        e.preventDefault();
        onActiveIndexChange(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        onActiveIndexChange(stories.length - 1);
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, narrow, onActiveIndexChange, stories.length]);

  const handleCardActivate = useCallback(
    (idx) => {
      if (idx === activeIndex) onOpenStory(idx);
      else onActiveIndexChange(idx);
    },
    [activeIndex, onActiveIndexChange, onOpenStory]
  );

  if (!stories.length) return null;

  return (
    <div className="space-y-5" data-testid="immersive-story-gallery">
      <p className="text-center text-[10px] font-medium uppercase tracking-[0.22em] text-[hsl(32_10%_40%)]">
        Preserved fragments
      </p>
      <p className="mx-auto max-w-lg text-center text-sm leading-[1.65] text-[hsl(32_10%_44%)]">
        Small pieces of one life, held here like letters in a box. Linger on a prompt if you like — then open it to
        read or listen in full.
      </p>

      {narrow ? (
        <div
          className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-pb-24 px-1 pb-[max(1rem,calc(0.75rem+env(safe-area-inset-bottom,0px)))] pt-1 [scrollbar-width:thin] lg:scroll-pb-4"
          role="list"
          aria-label="Preserved story fragments — move sideways to see each one"
        >
          {stories.map((story, idx) => {
            const { primary, secondary } = storyAnchorLines(story);
            const coverUrl = getStoryCoverUrl(story, backendBaseUrl, STORY_COVER_VARIANTS.CARD);
            return (
              <article
                key={story.id}
                role="listitem"
                className={cn(
                  'w-[min(90vw,21rem)] shrink-0 snap-center snap-always overflow-hidden rounded-[1.35rem] border border-[hsl(38_14%_80%)] bg-gradient-to-b from-[hsl(42_28%_99.5%)] via-[hsl(40_22%_97%)] to-[hsl(36_18%_94%)] shadow-[inset_0_1px_0_hsl(42_45%_100%_/_0.65),0_20px_48px_-12px_hsl(28_14%_16%_/_0.12)]',
                  idx === activeIndex && 'ring-1 ring-[#c9a961]/40'
                )}
              >
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt={story.cover_image_alt || primary}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[16/10] w-full object-cover"
                  />
                ) : null}
                <div className="p-6">
                {subject && <ArchiveSubjectIdentity person={subject} variant="compact" className="mb-4 opacity-90" />}
                <div className="mb-3 flex items-center gap-2">
                  {storyHasVoice(story) ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(38_16%_86%)]/90 bg-[hsl(42_24%_99%)]/90 px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.14em] text-[hsl(32_12%_40%)]">
                      <Mic className="h-3 w-3 text-[#8b7355]" aria-hidden />
                      Voice preserved
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(38_16%_86%)]/90 bg-[hsl(42_24%_99%)]/90 px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.14em] text-[hsl(32_12%_40%)]">
                      <Feather className="h-3 w-3 text-[#8b7355]" aria-hidden />
                      Written story
                    </span>
                  )}
                </div>
                <h4 className="font-serif text-[1.2rem] font-medium leading-snug text-[hsl(24_12%_16%)] line-clamp-4" title={primary}>
                  {primary}
                </h4>
                {secondary ? (
                  <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground/85 line-clamp-2">{secondary}</p>
                ) : null}
                {writtenExcerpt(story, 100) ? (
                  <p className="mt-4 line-clamp-3 text-xs leading-relaxed text-[hsl(32_10%_38%)]">{writtenExcerpt(story, 100)}</p>
                ) : null}
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[hsl(38_14%_78%)]"
                    onClick={() => onActiveIndexChange(idx)}
                  >
                    Focus
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full bg-[#1c1917] px-4 text-[#faf7f2] shadow-sm hover:bg-[#2c2824]"
                    onClick={() => onOpenStory(idx)}
                  >
                    Open story
                  </Button>
                </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div
          ref={regionRef}
          tabIndex={0}
          role="region"
          aria-label={`${stories.length} preserved ${stories.length === 1 ? 'fragment' : 'fragments'} of this life. Arrow keys move between them; Home and End go to the ends. Open enters the full story.`}
          className="relative mx-auto w-full max-w-6xl rounded-[2rem] border border-[hsl(32_12%_82%)]/90 bg-gradient-to-b from-[hsl(38_20%_95%)] via-[hsl(34_16%_92%)] to-[hsl(30_12%_88%)] px-2 py-7 shadow-[inset_0_1px_0_hsl(42_35%_98%_/_0.4),0_32px_64px_-20px_hsl(28_14%_14%_/_0.14)] outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-[#c9a961]/55 sm:px-5 sm:py-9"
          style={{ perspective: reduceMotion ? 'none' : '1400px' }}
        >
          {/* Archival depth: soft vignette + quiet warmth (no heavy blur). */}
          <div
            className="pointer-events-none absolute inset-0 rounded-[2rem]"
            style={{
              background: [
                'radial-gradient(ellipse 72% 58% at 50% 44%, hsl(42 32% 98% / 0.5) 0%, transparent 58%)',
                'radial-gradient(ellipse 100% 100% at 50% 50%, transparent 52%, hsl(28 14% 18% / 0.07) 100%)',
                'linear-gradient(180deg, hsl(40 22% 97% / 0.35) 0%, transparent 38%, hsl(28 12% 22% / 0.04) 100%)',
              ].join(', '),
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-[1px] rounded-[calc(2rem-1px)] opacity-[0.22]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              mixBlendMode: 'multiply',
            }}
            aria-hidden
          />

          <div
            ref={trackRef}
            className="relative flex min-h-[min(56vh,30rem)] items-center justify-center overflow-x-clip overflow-y-visible sm:min-h-[27.5rem]"
          >
            {/* Quiet pool of light behind the focal card */}
            <div
              className="pointer-events-none absolute left-1/2 top-1/2 h-[min(78%,22rem)] w-[min(92%,28rem)] -translate-x-1/2 -translate-y-1/2 rounded-[100%] opacity-90"
              style={{
                background:
                  'radial-gradient(ellipse at center, hsl(42 28% 99% / 0.55) 0%, hsl(38 18% 92% / 0.12) 55%, transparent 72%)',
              }}
              aria-hidden
            />

            <motion.div
              className="absolute left-0 top-0 flex h-full items-center will-change-transform"
              style={{
                transformStyle: 'preserve-3d',
                gap: GAP,
                width: stories.length * CARD_W + Math.max(0, stories.length - 1) * GAP,
              }}
              animate={{ x: trackX }}
              transition={transition}
            >
              {stories.map((story, idx) => {
                const offset = idx - activeIndex;
                const abs = Math.abs(offset);
                const yawCap = stories.length > 14 ? 4 : stories.length > 8 ? 5 : 5;
                const rotateY = reduceMotion ? 0 : clamp(offset, -2, 2) * -yawCap;
                const baseScale = offset === 0 ? 1.048 : 1 - Math.min(abs, 3) * 0.052;
                const opacity = offset === 0 ? 1 : 0.68;
                const z = 40 - abs;
                const { primary, secondary } = storyAnchorLines(story);
                const coverUrl = getStoryCoverUrl(story, backendBaseUrl, STORY_COVER_VARIANTS.CARD);

                return (
                  <motion.button
                    key={story.id}
                    type="button"
                    style={{
                      width: CARD_W,
                      flexShrink: 0,
                      transformStyle: 'preserve-3d',
                      zIndex: z,
                    }}
                    initial={false}
                    animate={{
                      rotateY,
                      scale: baseScale,
                      opacity,
                      filter:
                        offset === 0
                          ? 'saturate(1.02) contrast(1.01)'
                          : 'saturate(0.78) contrast(0.96) brightness(0.98)',
                    }}
                    transition={cardTransition}
                    onClick={() => handleCardActivate(idx)}
                    className={cn(
                      'relative cursor-pointer overflow-hidden rounded-[1.35rem] border bg-gradient-to-b text-left transition-[box-shadow,transform,border-color] duration-500 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a961]/55',
                      offset === 0
                        ? 'border-[hsl(34_14%_72%)] from-[hsl(44_32%_99.5%)] via-[hsl(40_26%_97%)] to-[hsl(36_20%_93%)] shadow-[inset_0_1px_0_hsl(42_48%_100%_/_0.85),inset_0_-12px_32px_-18px_hsl(32_14%_24%_/_0.06),0_36px_64px_-14px_hsl(28_14%_14%_/_0.2),0_12px_28px_hsl(28_12%_18%_/_0.1)] hover:shadow-[inset_0_1px_0_hsl(42_48%_100%_/_0.9),0_40px_72px_-12px_hsl(28_14%_14%_/_0.22)]'
                        : 'border-[hsl(38_12%_86%)]/85 from-[hsl(42_26%_99%)] to-[hsl(36_18%_94%)] shadow-[inset_0_1px_0_hsl(42_40%_100%_/_0.35),0_16px_40px_-16px_hsl(28_14%_18%_/_0.12)] hover:border-[hsl(36_14%_78%)]/90 hover:brightness-[1.02]',
                      offset === 0 ? 'p-0' : 'p-5'
                    )}
                    data-testid={`gallery-story-card-${story.id}`}
                    aria-current={offset === 0 ? 'true' : undefined}
                    aria-label={
                      offset === 0
                        ? `${storyLabel(story)}. Center fragment — press Enter or click to open the full story.`
                        : `${storyLabel(story)} — bring this fragment forward.`
                    }
                  >
                    {/* Paper edge sheen */}
                    <span
                      className="pointer-events-none absolute inset-x-3 top-0 h-px rounded-full bg-gradient-to-r from-transparent via-[hsl(42_40%_100%_/_0.7)] to-transparent opacity-80"
                      aria-hidden
                    />
                    {coverUrl && offset === 0 ? (
                      <img
                        src={coverUrl}
                        alt={story.cover_image_alt || primary}
                        loading="lazy"
                        decoding="async"
                        className="aspect-[16/10] w-full object-cover"
                      />
                    ) : null}
                    <div className={cn(offset === 0 ? 'p-6' : '')}>
                    {subject && offset === 0 && (
                      <ArchiveSubjectIdentity person={subject} variant="compact" className="mb-4 opacity-[0.92]" />
                    )}
                    {subject && offset !== 0 && (
                      <p className="mb-2 truncate text-[9px] font-medium uppercase tracking-[0.14em] text-[hsl(32_10%_50%)]">
                        {archiveSubjectDisplayName(subject) || 'Archive subject'}
                      </p>
                    )}
                    <div className={cn('mb-3 flex items-center gap-2', offset !== 0 && 'opacity-80')}>
                      {storyHasVoice(story) ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(38_14%_84%)]/95 bg-[hsl(42_22%_99%)]/80 px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.12em] text-[hsl(32_12%_42%)]">
                          <Mic className="h-3 w-3 shrink-0 text-[#8b7355]" aria-hidden />
                          Voice preserved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(38_14%_84%)]/95 bg-[hsl(42_22%_99%)]/80 px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.12em] text-[hsl(32_12%_42%)]">
                          <Feather className="h-3 w-3 shrink-0 text-[#8b7355]" aria-hidden />
                          Written story
                        </span>
                      )}
                    </div>
                    <h4
                      className={cn(
                        'font-serif text-[hsl(24_12%_15%)] line-clamp-4',
                        offset === 0
                          ? 'text-[1.38rem] font-normal leading-[1.42] tracking-[-0.015em] sm:text-[1.48rem]'
                          : 'text-[0.92rem] font-medium leading-snug tracking-normal line-clamp-2'
                      )}
                      title={primary}
                    >
                      {primary}
                    </h4>
                    {secondary ? (
                      <p
                        className={cn(
                          'leading-relaxed text-muted-foreground line-clamp-2',
                          offset === 0 ? 'mt-3 text-[12px]' : 'mt-2 text-[10px] opacity-80'
                        )}
                      >
                        {secondary}
                      </p>
                    ) : null}
                    {writtenExcerpt(story) ? (
                      <p
                        className={cn(
                          'line-clamp-3 leading-relaxed text-[hsl(32_10%_38%)]',
                          offset === 0 ? 'mt-4 text-[12px]' : 'mt-3 text-[10px] opacity-75'
                        )}
                      >
                        {writtenExcerpt(story)}
                      </p>
                    ) : null}
                    {offset === 0 && (
                      <p className="mt-6 border-t border-[hsl(38_14%_86%)]/45 pt-4 text-[11px] leading-[1.65] text-[hsl(32_10%_42%)]">
                        Nothing here is meant to be rushed. When you are ready, choose Open story below — or click
                        this card — to be with the whole memory.
                      </p>
                    )}
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          </div>

          <div className="relative z-10 mt-2 flex items-center justify-center gap-3 pb-1 pt-1">
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="h-11 w-11 rounded-full border-[hsl(38_14%_78%)] bg-[hsl(42_26%_99%)] shadow-sm transition hover:border-[hsl(34_14%_68%)] hover:bg-[hsl(42_28%_100%)] disabled:opacity-40"
              onClick={goPrev}
              disabled={activeIndex <= 0}
              aria-label="Previous story"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              className="rounded-full bg-[#1c1917] px-7 py-2.5 text-[0.9375rem] font-medium text-[#faf7f2] shadow-md transition hover:bg-[#2c2824] hover:shadow-lg"
              onClick={() => onOpenStory(activeIndex)}
              data-testid="gallery-open-story-btn"
              aria-label={`Open full story: ${storyLabel(stories[activeIndex])}`}
            >
              Open story
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="h-11 w-11 rounded-full border-[hsl(38_14%_78%)] bg-[hsl(42_26%_99%)] shadow-sm transition hover:border-[hsl(34_14%_68%)] hover:bg-[hsl(42_28%_100%)] disabled:opacity-40"
              onClick={goNext}
              disabled={activeIndex >= stories.length - 1}
              aria-label="Later preserved fragment"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
